import { useCallback } from "react";
import { AutocompleteProps } from "@mui/material";
import { useSearchParams } from "react-router-dom";

const getParamKey = (keys: string[]) => keys.flatMap((k) => k || []).join(".");

const getSearchParamKey = (prefix = "") => getParamKey([prefix, "queryTerms"]);

export type UseSearchProps =
  | { prefix?: string; baseUrl?: URL | string; multiple?: boolean }
  | undefined;

export function useSearch({
  prefix = "",
  baseUrl,
  multiple = true,
}: UseSearchProps = {}) {
  const url = new URL(baseUrl || location.href);
  const searchParamKey = getSearchParamKey(prefix);
  const [searchParams, setSearchParams] = useSearchParams();
  const searchFilters = searchParams.get(searchParamKey)?.split(",") || [];

  const onTagDelete = useCallback(
    (value: string) => {
      const nextQueryTerms = searchFilters.filter((v) => v !== value);
      if (nextQueryTerms.length === 0) {
        url.searchParams.delete(searchParamKey);
      } else {
        url.searchParams.set(searchParamKey, nextQueryTerms.join(","));
      }
      setSearchParams(url.searchParams);
    },
    [url, searchParamKey, searchFilters],
  );

  const handleFilterQuery: NonNullable<
    AutocompleteProps<string, true, false, true>["onChange"]
  > = useCallback(
    (_event, query, reason) => {
      if (reason === "clear") {
        url.searchParams.delete(searchParamKey);
      } else {
        const searchParamValue = query.slice(multiple ? 0 : -1).join(",");
        url.searchParams.set(searchParamKey, searchParamValue);
      }

      setSearchParams(url.searchParams);
    },
    [url, searchParamKey, multiple],
  );

  const handleClearSearch = useCallback(
    (params = [searchParamKey]) => {
      params.forEach((filterParam) => {
        url.searchParams.delete(filterParam);
      });

      setSearchParams(url.searchParams);
    },
    [url, searchParamKey],
  );

  return { onTagDelete, handleFilterQuery, handleClearSearch, searchFilters };
}
